import {
  PromoCode,
  Subscription,
  SubscriptionFullPlanObject,
  SubscriptionPendingPlanInterval,
  SubscriptionPlanObject,
  SubscriptionPlanType,
  SubscriptionPrices,
} from '@repo/subscription/api';
import { differenceInCalendarDays } from 'date-fns';
import Cookies from 'js-cookie';

import { getAddons, getPricingConfig } from '../../global/pricingConfig';
import {
  MonetaryValue,
  PricingPlanAddon,
  PricingPlanValue,
} from '../../types/Pricing';

import { BillingPrice } from './hooks/useBillingPrices';

export const isSameDate = (dateA: Date, dateB: Date): boolean =>
  differenceInCalendarDays(dateA, dateB) === 0;

/**
 * format a date for display
 */
export const getDateLabel = (date?: string | Date): string => {
  if (!date) {
    return '';
  }

  const dateObject = new Date(date);
  return dateObject.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

export const getIntervalName = (
  interval: SubscriptionPendingPlanInterval | undefined
): string => {
  if (interval && interval in SubscriptionPendingPlanInterval) {
    return SubscriptionPendingPlanInterval[interval];
  }

  return '';
};

/** Type guard for PlanObject, as in "not SimplePlanObject" */
export const isPlanObject = (
  plan: SubscriptionPlanObject
): plan is SubscriptionFullPlanObject =>
  plan && 'interval' in plan && 'amount' in plan && 'quantity' in plan;

export const getAddonFromSubscription = (
  subscription: Subscription | null
): PricingPlanAddon | null => {
  if (!subscription) {
    return null;
  }

  const addons = getAddons();
  const addonPriceId =
    subscription?.plan && isPlanObject(subscription.plan)
      ? subscription.plan.addon?.priceId
      : null;

  if (!addonPriceId) {
    return null;
  }

  return addons.find((addon) => addon.stripeId === addonPriceId) ?? null;
};

export const priceWithPromoCode = (
  price: BillingPrice,
  promoCode?: PromoCode | null
): MonetaryValue => {
  const total = price.quantity * price.amount;
  if (typeof promoCode?.percentOff === 'number') {
    return {
      currency: price.currency,
      amount: Math.round(total - total * (promoCode.percentOff / 100)),
    };
  }

  if (typeof promoCode?.amountOff === 'number') {
    return {
      currency: price.currency,
      amount: Math.max(0, total - promoCode.amountOff),
    };
  }

  return {
    amount: total,
    currency: price.currency,
  };
};

/**
 * @description Calculate the discount amount based on the promo code.
 * @param value Value of the item being purchased by customer.
 * @param promoCode Promo code information to apply to the value.
 * @returns The discount amount. Null if invalid promo code or no discount.
 * @example calculateDiscount({ amount: 1000, currency: 'usd' }, { percentOff: 10 }) => 100
 */
export const calculateDiscount = (
  value: MonetaryValue,
  promoCode?: PromoCode | null
): number | null => {
  if (typeof promoCode?.percentOff === 'number') {
    return Math.round(value.amount * (promoCode.percentOff / 100));
  }

  if (typeof promoCode?.amountOff === 'number') {
    return Math.max(0, promoCode.amountOff);
  }

  return null;
};

export const withPromoCode = (
  value: MonetaryValue,
  promoCode?: PromoCode | null
): MonetaryValue => {
  if (typeof promoCode?.percentOff === 'number') {
    return {
      ...value,
      amount: Math.round(
        value.amount - value.amount * (promoCode.percentOff / 100)
      ),
    };
  }

  if (typeof promoCode?.amountOff === 'number') {
    return {
      ...value,
      amount: Math.max(0, value.amount - promoCode.amountOff),
    };
  }

  return value;
};

/**
 * Get the price of a plan based on the selected plan, billing interval, and country.
 */
export const getPlanPrice = ({
  plan,
  isYearly,
  subscriptionPrices,
  country,
}: {
  plan: SubscriptionPlanType;
  isYearly: boolean;
  subscriptionPrices: SubscriptionPrices;
  country: string | null;
}): PricingPlanValue =>
  getPricingConfig({ subscriptionPrices, country })[plan].price[
    isYearly ? 'yearly' : 'monthly'
  ];

export const isYearlySubscription = (
  subscription: Subscription | null
): boolean => {
  return (
    !!subscription?.plan &&
    'interval' in subscription.plan &&
    subscription?.plan?.interval === 'year'
  );
};

const PROMO_CODE_COOKIE_NAME = 'htcPromoCode';

export function getPromoCodeFromCookie(): string | null {
  return Cookies.get(PROMO_CODE_COOKIE_NAME) || null;
}

export function setPromoCodeCookie(code: string): void {
  Cookies.set(PROMO_CODE_COOKIE_NAME, code, {
    expires: 30,
  });
}

export function removeCookieCode(): void {
  Cookies.remove(PROMO_CODE_COOKIE_NAME);
}
